import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import aiBrainLogo from '../assets/ai-brain-actual.png';
import { getBackendApiUrl } from '../config/backendApi';

const PasswordResetForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [realmId, setRealmId] = useState<string | null>(null);
  const [firmName, setFirmName] = useState<string | null>(null);
  const [isRequestMode, setIsRequestMode] = useState(true); // true = request reset, false = reset password
  const [formData, setFormData] = useState({
    email: '',
    firmId: '', // Subscriber ID for self-service reset
    newPassword: '',
    confirmPassword: ''
  });
  const [isInvitationMode, setIsInvitationMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Extract all parameters from URL
    const resetToken = searchParams.get('token');
    const realm = searchParams.get('realm');
    const firm = searchParams.get('firm') || searchParams.get('subscriber');
    const emailParam = searchParams.get('email');
    const invitation = searchParams.get('invitation') === 'true';

    if (resetToken && realm) {
      setToken(resetToken);
      setRealmId(realm);
      setFirmName(firm ? decodeURIComponent(firm) : null);
      setIsRequestMode(false); // Show reset/set password form
      setIsInvitationMode(invitation);
      if (emailParam) {
        setFormData(prev => ({ ...prev, email: decodeURIComponent(emailParam) }));
      }
    } else {
      setToken(null);
      setRealmId(null);
      setFirmName(null);
      setIsRequestMode(true); // Show request reset form
      setIsInvitationMode(false);
    }
  }, [searchParams]);

  // ✅ NEW: Firm context display component
  const renderFirmContext = () => {
    if (!realmId || isRequestMode) return null;

    return (
      <div className="mb-6 p-4 bg-blue-500/10 backdrop-blur-sm rounded-lg border border-blue-500/30">
        <div className="flex items-center mb-2">
          <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h3 className="text-sm font-semibold text-blue-300">
            {isInvitationMode ? 'Your Account' : 'Password Reset Context'}
          </h3>
        </div>
        <p className="text-sm text-blue-300 mb-1">
          <span className="font-medium">Subscriber:</span> {firmName || 'Unknown Subscriber'}
        </p>
        <p className="text-xs text-blue-400">
          <span className="font-medium">Realm:</span> {realmId}
        </p>
      </div>
    );
  };

  // validateRequestForm removed - not needed for admin-initiated password reset flow

  const validateResetForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // ✅ NEW: Validate realm context is available
    if (!realmId) {
      newErrors.submit = 'Invalid reset link. Please request a new password reset from your administrator.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRequestForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }

    if (!formData.firmId.trim()) {
      newErrors.firmId = 'Subscriber ID is required';
    } else if (!/^[a-z0-9-]{3,30}$/.test(formData.firmId)) {
      newErrors.firmId = 'Subscriber ID must be 3-30 characters, lowercase letters, numbers, and hyphens only';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRequestForm()) return;

    setIsLoading(true);
    try {
      const response = await fetch(getBackendApiUrl('/api/auth/request-reset'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase().trim(),
          firmId: formData.firmId.toLowerCase().trim()
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setSuccessMessage('If an account with that email exists in the specified subscriber, you will receive password reset instructions. Please check your inbox.');
      } else {
        setErrors({ submit: result.message || 'Failed to send reset email' });
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to send reset email. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateResetForm()) return;
    if (!token || !realmId) {
      setErrors({ submit: 'Invalid reset token or missing realm context' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(getBackendApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          email: formData.email,
          newPassword: formData.newPassword,
          realmId                                   // ← NEW: Include realm in API call
        }),
      });

      if (!response.ok) {
        throw new Error(`Password reset failed: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setSuccess(true);
        setSuccessMessage(
          isInvitationMode
            ? 'Your password has been set. You can now sign in to WeBelieve!'
            : 'Password reset successful! You can now log in with your new password.'
        );
      } else {
        setErrors({ submit: result.message || 'Password reset failed' });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Password reset failed. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-600/80 to-slate-950 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        <div className="relative max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {isInvitationMode ? "You're All Set!" : 'Password Reset Successful!'}
            </h1>
            <p className="text-blue-300">{successMessage}</p>
            {firmName && (
              <p className="text-sm text-blue-300 mt-2">
                You can now sign in to <strong>{firmName}</strong>
              </p>
            )}
          </div>
          <div className="space-y-4">
            <button
              onClick={() => window.location.href = `https://app.webelievesoftware.com/login${realmId ? `?realm=${realmId}` : ''}`}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              Sign In to {firmName || 'WeBelieve'}
            </button>
            <button
              onClick={() => window.location.href = 'https://webelievesoftware.com'}
              className="w-full py-2 px-4 text-blue-300 hover:text-white font-medium transition-colors"
            >
              Return to WeBelieve Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-600/80 to-slate-950 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ring-4 ring-blue-500/30 overflow-hidden">
            <img
              src={aiBrainLogo}
              alt="WeBelieve Logo"
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {isInvitationMode ? 'Welcome to WeBelieve!' : (isRequestMode ? 'Reset Password' : 'Set New Password')}
          </h1>
          <p className="text-blue-300">
            {isInvitationMode
              ? 'Set your password to get started'
              : (isRequestMode ? 'Enter your email to receive reset instructions' : 'Enter your new password below')}
          </p>
        </div>

        {/* ✅ NEW: Display firm context for password reset */}
        {renderFirmContext()}

        <form onSubmit={isRequestMode ? handleRequestReset : handlePasswordReset} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-blue-300 mb-2">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                readOnly={isInvitationMode}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  isInvitationMode ? 'opacity-70 cursor-not-allowed' : ''
                } ${errors.email ? 'border-red-400' : 'border-white/20'}`}
                placeholder="your.com"
                required
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
          </div>

          {isRequestMode && (
            <div>
              <label className="block text-sm font-medium text-blue-300 mb-2">Subscriber ID</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={formData.firmId}
                  onChange={(e) => updateFormData('firmId', e.target.value.toLowerCase())}
                  className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                    errors.firmId ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="e.g., acme-team"
                  required
                />
              </div>
              {errors.firmId && <p className="mt-1 text-sm text-red-400">{errors.firmId}</p>}
              <p className="mt-1 text-xs text-blue-300">
                This is the same ID you use to login. Ask your administrator if unsure.
              </p>
            </div>
          )}

          {!isRequestMode && (
            <>
              <div>
                <label className="block text-sm font-medium text-blue-300 mb-2">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => updateFormData('newPassword', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.newPassword ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="Enter new password"
                    required
                  />
                </div>
                {errors.newPassword && <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-300 mb-2">Confirm New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.confirmPassword ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>}
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-slate-600 disabled:to-slate-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading && (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isLoading
              ? (isRequestMode ? 'Sending Email...' : 'Setting Password...')
              : (isInvitationMode ? 'Set Password & Get Started' : (isRequestMode ? 'Send Reset Email' : 'Reset Password'))
            }
          </button>

          {errors.submit && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
              <p className="text-red-200 text-sm">{errors.submit}</p>
            </div>
          )}
        </form>

        <div className="mt-8 text-center">
          <p className="text-blue-300 text-sm">
            Need help? Contact{' '}
            <a href="mailto:support@webelievesoftware.com" className="text-cyan-400 hover:underline">
              support@webelievesoftware.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;
